import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  fetchWebhookSummary,
  type ApiError,
  type WebhookSummary,
} from "../../../services/merchantApi";

// useWebhookSummary.ts
// ONE module-level poller behind every consumer of the webhook summary —
// the sidebar badge (Layout), the Home banner (Dashboard), the API Settings
// card and the webhooks pages all mount this hook at the same time, and
// they must share a single request, not fire four.
//
// Rules:
//   • idle while `wallet` is null (the backend would otherwise resolve the
//     caller from the email alone and answer for the wrong identity);
//   • first fetch immediately, then every 60 s;
//   • paused while the tab is hidden; catches up when it becomes visible;
//   • errors back off 120 → 240 → 480 s, capped at 10 min; a 401 stops the
//     poller until the identity changes or a refresh is requested;
//   • `{ merchant: false }` (business user without API access) slows down
//     to every 10 min — approval may still arrive;
//   • notifyWebhooksChanged() refetches at once — call it after EVERY
//     mutation so badge, banner and card never wait for the next tick;
//   • a different wallet resets everything;
//   • SILENT: this module never raises a user-facing notification. Layout
//     mounts it and has no notification host; a failed poll just keeps the
//     last good summary.
//
// Nothing here runs at import time: timers and listeners exist only while
// at least one consumer is mounted.

const POLL_MS = 60_000;
const SLOW_POLL_MS = 10 * 60_000;
const MAX_BACKOFF_MS = 10 * 60_000;

interface Snapshot {
  /** wallet the data belongs to */
  key: string | null;
  summary: WebhookSummary | null;
  /** true until the first response (or failure) for `key` */
  loading: boolean;
}

let snapshot: Snapshot = { key: null, summary: null, loading: false };
const subscribers = new Set<() => void>();

let identity: { wallet: string; email: string | null } | null = null;
let consumers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> | null = null;
let refetchQueued = false;
let failures = 0;
let stopped = false;
let lastSettledAt = 0;
// Bumped on every identity change so a response for the previous wallet
// can never land in the new wallet's state.
let generation = 0;
let visibilityBound = false;

function setSnapshot(next: Snapshot) {
  snapshot = next;
  subscribers.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function getSnapshot(): Snapshot {
  return snapshot;
}

function clearTimer() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function currentDelay(): number {
  const base = snapshot.summary && snapshot.summary.merchant === false ? SLOW_POLL_MS : POLL_MS;
  if (failures === 0) return base;
  return Math.max(base, Math.min(POLL_MS * 2 ** failures, MAX_BACKOFF_MS));
}

function schedule(ms: number) {
  clearTimer();
  if (consumers === 0 || stopped || !identity) return;
  timer = setTimeout(() => {
    timer = null;
    // Hidden tab: skip this tick and do NOT re-arm. onVisibilityChange
    // resumes the cycle the moment the user comes back.
    if (typeof document !== "undefined" && document.hidden) return;
    void fetchNow();
  }, Math.max(0, ms));
}

function fetchNow(): Promise<void> {
  if (!identity) return Promise.resolve();
  if (inFlight) return inFlight;

  const gen = generation;
  const { wallet, email } = identity;
  clearTimer();

  const run = (async () => {
    try {
      const summary = await fetchWebhookSummary(wallet, email);
      if (gen !== generation) return;
      failures = 0;
      setSnapshot({ key: wallet, summary, loading: false });
    } catch (e) {
      if (gen !== generation) return;
      if ((e as ApiError | undefined)?.status === 401) stopped = true;
      else failures += 1;
      // Keep the last good summary: a blip must not blank the badge.
      if (snapshot.loading) setSnapshot({ ...snapshot, loading: false });
    } finally {
      // After an identity change `inFlight` already belongs to the new
      // wallet (attach() reset it) — a stale run must leave it alone.
      if (gen === generation) {
        inFlight = null;
        lastSettledAt = Date.now();
        if (refetchQueued) {
          // A mutation landed while this request was already on the wire,
          // so its answer may predate the change. Go again right away.
          refetchQueued = false;
          void fetchNow();
        } else {
          schedule(currentDelay());
        }
      }
    }
  })();
  inFlight = run;
  return run;
}

function onVisibilityChange() {
  if (document.hidden) return;
  if (consumers === 0 || stopped || !identity || inFlight || timer) return;
  const remaining = currentDelay() - (Date.now() - lastSettledAt);
  if (remaining <= 0) void fetchNow();
  else schedule(remaining);
}

function bindVisibility() {
  if (visibilityBound || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onVisibilityChange);
  visibilityBound = true;
}

function unbindVisibility() {
  if (!visibilityBound || typeof document === "undefined") return;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  visibilityBound = false;
}

function attach(wallet: string, email: string | null): () => void {
  consumers += 1;

  if (!identity || identity.wallet !== wallet) {
    // New identity (login, account switch): drop every trace of the old one.
    generation += 1;
    clearTimer();
    inFlight = null;
    refetchQueued = false;
    failures = 0;
    stopped = false;
    lastSettledAt = 0;
    identity = { wallet, email };
    setSnapshot({ key: wallet, summary: null, loading: true });
  } else if (identity.email !== email) {
    // Same wallet, email hydrated late: use it from the next request on.
    identity = { wallet, email };
  }

  bindVisibility();

  // A surface just mounted: anything older than one normal interval is
  // refreshed now — also in slow mode, because the user may be arriving
  // here precisely because their API access was just approved.
  const stale = Date.now() - lastSettledAt >= POLL_MS;
  if (!inFlight && !stopped && (stale || !timer)) {
    if (stale) void fetchNow();
    else schedule(currentDelay() - (Date.now() - lastSettledAt));
  }

  return () => {
    consumers = Math.max(0, consumers - 1);
    if (consumers === 0) {
      // Nobody is listening: stop the clock. The cached summary stays so
      // the next mount paints instantly (and refreshes if it is stale).
      clearTimer();
      unbindVisibility();
    }
  };
}

/**
 * Forces an immediate refetch of the shared summary. Call after EVERY
 * webhook mutation (create / edit / pause / resume / delete / rotate /
 * test / resend / retry / ack). No-op while no consumer is mounted.
 */
export function notifyWebhooksChanged(): void {
  if (!identity || consumers === 0) return;
  // A successful mutation proves the identity works again.
  stopped = false;
  if (inFlight) {
    refetchQueued = true;
    return;
  }
  void fetchNow();
}

/**
 * Live webhook summary for the signed-in merchant.
 * @param wallet Redux `user.wallet || user.smartWallet`; pass `null` to keep
 *               the hook idle (not a business account, auth still hydrating)
 * @param email  Redux `user.email`
 * @returns `summary` — null until the first response, and null when idle;
 *          `{ merchant: false, … }` for a business user without API access.
 *          `loading` — true only before the first response for this wallet
 *          (`!loading && !summary` therefore means the first load failed).
 *          `refresh` — refetch now; resolves when the request settles.
 */
export function useWebhookSummary(
  wallet: string | null | undefined,
  email: string | null | undefined,
): { summary: WebhookSummary | null; loading: boolean; refresh: () => Promise<void> } {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!wallet) return;
    return attach(wallet, email ?? null);
  }, [wallet, email]);

  const refresh = useCallback(async () => {
    if (!identity) return;
    stopped = false;
    if (inFlight) {
      // The request on the wire may predate whatever prompted this refresh:
      // queue a follow-up and wait for that one too.
      refetchQueued = true;
      await inFlight;
      if (inFlight) await inFlight;
      return;
    }
    await fetchNow();
  }, []);

  if (!wallet) return { summary: null, loading: false, refresh };
  // The effect has not bound this wallet yet (first render, or the wallet
  // just changed): never leak the previous identity's summary.
  if (snap.key !== wallet) return { summary: null, loading: true, refresh };
  return { summary: snap.summary, loading: snap.loading, refresh };
}
